import { Theme } from "@radix-ui/themes";
import { Html, Text, useCursor, useGLTF, useTexture } from "@react-three/drei";
import { ThreeEvent } from "@react-three/fiber";
import { useMediaState } from "@tezzro/player";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BufferGeometry,
  Color,
  InstancedMesh,
  Matrix4,
  SRGBColorSpace,
  Vector3,
} from "three";
import { CameraCalibration } from "@/shared/api";
import { CameraPreviewCard } from "./camera-preview-card";
import useCameraHandlers from "../../hooks/useCameraHandlers";

export function CameraInstances({
  cameras,
  filteredCameraIds,
  getCameraImage,
  onSelectCamera,
}: {
  getCameraImage: (cameraId: string) => string | null;
  filteredCameraIds: string[];
  cameras: CameraCalibration[];
  onSelectCamera: () => void;
}) {
  const ref = useRef<InstancedMesh | null>(null);
  const { nodes } = useGLTF("/carmeraFrustum-transformed.glb", true);
  const [hovered, setHovered] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const [cameraLabels, setCameraLabels] = useState<JSX.Element[]>([]);
  const activeCameraId = useMediaState("cameraId");
  const { handleMapCameraSelect } = useCameraHandlers();
  const prevColor = useRef(new Map());
  const cameraIds = useRef<string[]>([]);

  useCursor(hovered);

  useEffect(() => {
    if (!ref.current || !activeCameraId) return;

    const color = new Color();
    const instanceId = cameraIds.current.indexOf(activeCameraId);

    color.setHex(0xffc53d);
    ref.current.setColorAt(instanceId, color);
    ref.current.instanceColor!.needsUpdate = true;
  }, [activeCameraId]);

  const onClick = (e: ThreeEvent<MouseEvent>) => {
    if (e.instanceId === undefined) return;

    e.stopPropagation();

    const cameraId = cameraIds.current[e.instanceId];

    handleMapCameraSelect(cameraId);
    setSearchParams((prev) => {
      prev.set("camera", cameraId.toString());
      return prev;
    });

    onSelectCamera();

    setTimeout(() => {
      prevColor.current.delete(e.instanceId);
    });
  };

  const [hoveredCamera, setHoveredCamera] = useState<{
    cameraId: string;
    position: Vector3;
    image: string | null;
  } | null>(null);

  const setHoveredCameraParams = (instanceId: number) => {
    if (!ref.current) return;

    const cameraId = cameraIds.current[instanceId];
    const matrix = new Matrix4();
    const position = new Vector3();

    ref.current.getMatrixAt(instanceId, matrix);
    position.setFromMatrixPosition(matrix);

    const image = getCameraImage(cameraId);

    setHoveredCamera({ cameraId, position, image });
  };

  const onPointerOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();

    if (!ref.current || e.instanceId === undefined) return;

    setHovered(true);
    setHoveredCameraParams(e.instanceId);

    const color = new Color();
    ref.current.getColorAt(e.instanceId, color);
    prevColor.current.set(e.instanceId, color.getHex());

    const instanceId = cameraIds.current.indexOf(activeCameraId || "");

    if (instanceId === e.instanceId) {
      color.setHex(0x8f6424);
    } else {
      color.setHex(0xff0000);
    }

    ref.current.setColorAt(e.instanceId, color);
    ref.current.instanceColor!.needsUpdate = true;
  };

  const onPointerOut = (e: ThreeEvent<PointerEvent>) => {
    if (!ref.current || e.instanceId === undefined) return;

    const color = new Color();
    ref.current.getColorAt(e.instanceId, color);

    if (prevColor.current.has(e.instanceId)) {
      color.setHex(prevColor.current.get(e.instanceId));
      prevColor.current.delete(e.instanceId);
    } else {
      color.setHex(0xffffff);
    }

    setHovered(false);
    setHoveredCamera(null);
    ref.current.setColorAt(e.instanceId, color);
    ref.current.instanceColor!.needsUpdate = true;
  };

  const geometry = useMemo(() => {
    const geo: BufferGeometry = (nodes.Cube as any).geometry.clone();
    geo.computeVertexNormals();
    geo.scale(0.1, 0.1, 0.1);
    geo.rotateX(-Math.PI * 0.5);
    return geo;
  }, [nodes]);

  useLayoutEffect(() => {
    if (!cameras || !cameras.length || !ref.current) return;

    const cameraLabels: JSX.Element[] = [];
    const sortedCameras = [...cameras].sort(
      (a, b) => Number(a.camera_id) - Number(b.camera_id)
    );
    const hasFilter = filteredCameraIds.length > 0;

    const filteredCameras = hasFilter
      ? sortedCameras.filter((camera, index) => {
          const isVisible = filteredCameraIds.includes(camera.camera_id);
          if (!isVisible) {
            ref.current?.setMatrixAt(index, new Matrix4());
          }
          return isVisible;
        })
      : sortedCameras;

    ref.current.clear();
    cameraIds.current = [];

    for (let i = 0; i < filteredCameras.length; ++i) {
      const camera = filteredCameras[i];
      const cameraId = camera.camera_id;
      const instanceId = i;

      cameraIds.current.push(cameraId);

      const transform = new Matrix4();
      const position = new Vector3(
        camera.location[0],
        camera.location[2],
        -camera.location[1]
      );
      const lookAt = new Vector3(
        camera.look_at[0],
        camera.look_at[2],
        -camera.look_at[1]
      );

      const defaultColor = new Color(0xffffff);
      const isActive = activeCameraId === cameraId;

      if (isActive) {
        defaultColor.setHex(0xffc53d);
      }

      ref.current.setColorAt(instanceId, defaultColor);
      transform.setPosition(position);
      transform.lookAt(position, lookAt, new Vector3(0, 1, 0));
      ref.current.setMatrixAt(instanceId, transform);
      cameraLabels.push(
        <Text
          key={cameraId}
          color={isActive ? "#ffc53d" : "white"}
          fontSize={0.1}
          fontWeight={700}
          position={[position.x + 0.2, position.y + 1, position.z]}
          rotation={[-Math.PI / 2, 0, 0]}
          anchorX="center"
          anchorY="middle"
        >
          {cameraId}
        </Text>
      );
    }

    ref.current.instanceMatrix!.needsUpdate = true;
    setCameraLabels(cameraLabels);
  }, [activeCameraId, cameras, filteredCameraIds]);

  const texture = useTexture("/matcap-texture.png");
  texture.colorSpace = SRGBColorSpace;

  const portalRef = useRef(document.body);

  return (
    <>
      {cameraLabels}
      <instancedMesh
        onPointerOver={onPointerOver}
        onClick={onClick}
        onPointerOut={onPointerOut}
        ref={ref}
        args={[
          geometry,
          undefined,
          filteredCameraIds.length > 0
            ? filteredCameraIds.length
            : cameras.length,
        ]}
      >
        <meshMatcapMaterial attach="material" color="white" matcap={texture} />
      </instancedMesh>
      {hoveredCamera && (
        <Html
          portal={portalRef}
          position={[
            hoveredCamera.position.x,
            hoveredCamera.position.y,
            hoveredCamera.position.z,
          ]}
          pointerEvents="none"
          className="pointer-events-none"
        >
          <Theme className="bg-transparent">
            <CameraPreviewCard image={hoveredCamera.image} />
          </Theme>
        </Html>
      )}
    </>
  );
}
